import React from 'react';
import useReveal from '../../hooks/useReveal';

const DataProcessing = () => {
  document.title = 'Data Processing Addendum - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-xl font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>Data Processing Addendum (DPA)</h1>

      <section className="mb-8">
        <p className="mb-4">
          This <strong>DATA PROCESSING ADDENDUM (“DPA”)</strong>, as updated
          from time to time, supplements and its term and conditions are subject
          to Cloudvantage Limited’s (its parent, subsidiaries, affiliates and/or
          brands) (collectively, “Company”){' '}
          <a
            href="/terms-of-use"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Universal Terms of Use
          </a>{' '}
          (“UToU”), which are incorporated herein by this reference, and governs
          Company’s use of Customer’s Data (as defined herein) (as a controller
          of such data). Company and Customer may be individually referred to as
          a “Party” or collectively, the “Parties.”
        </p>
      </section>

      <section className="mb-8">
        <h5 className="text-l font-semibold mb-4">Definitions</h5>
        <p className="mb-4">
          Unless otherwise defined in the Agreement (as defined herein), all
          capitalized terms used in this DPA will have the meanings given to
          them below:
        </p>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            “Affiliate” means an entity that directly or indirectly controls, is
            controlled by or is under common control with an entity.
          </li>
          <li>
            “Agreement” means the UToU and all other written or electronic
            agreement(s) between Company and Customer, which govern use of the
            Website, Products, or Order Form (as applicable), as such terms or
            agreement may be updated from time to time. For the avoidance of
            doubt, all references to the “Agreement” shall also include the
            Standard Contractual Clauses (where applicable, as defined herein).
          </li>
          <li>
            “Consumer,” “Business,” “Sell,“ and/or “Service Provider” shall have
            the meanings given to them in the CCPA or CPRA (as applicable).
          </li>
          <li>
            “Company Network” means Company’s data center facilities, servers,
            networking equipment, and software systems that are within Company’s
            control and are used to serve and/or provide the Websites and
            Products.
          </li>
          <li>
            “Company Security Standards” means the security standards attached
            to this DPA as Annex 1.
          </li>
          <li>
            “Customer” means a Website visitor, user and/or the party set forth
            in the related Order Form.
          </li>
          <li>
            “Customer Data” means the personal data Company processes on behalf
            of Customer via the Website or Products, as more particularly
            described in this DPA.
          </li>
          <li>
            “Data Protection Laws” means all applicable Nigerian laws,
            regulations, and legislation relating to data protection and privacy
            related to processing of Customer Data under the Agreement, in each
            case as amended, repealed, consolidated or replaced from time to
            time.
          </li>
          <li>
            “Data Subject” is defined as the person associated with the Personal
            Data.
          </li>
          <li>
            “Europe” means the European Economic Area and its member states
            (“EEA”), Switzerland and the United Kingdom (“UK”).
          </li>
          <li>
            “Personal Data” means any information about, or related to, an
            identifiable individual, which includes any information that can be
            linked to an individual or used to directly or indirectly identify
            an individual, natural person.
          </li>
          <li>
            “Processing” means any operation or set of operations that is
            performed upon Personal Data, whether or not by automatic means,
            such as collection, recording, securing, organization, storage,
            adaptation or alteration, access to, retrieval, consultation, use,
            disclosure by transmission, dissemination or otherwise making
            available, alignment or combination, blocking, erasure, or
            destruction. “Processes” and “Process” shall be construed
            accordingly.
          </li>
          <li>
            “Security Incident” means any unauthorized or unlawful breach of
            security that leads to the accidental or unlawful destruction, loss,
            or alteration of, or unauthorized disclosure of or access to,
            Customer Data on systems managed or otherwise controlled by Company.
          </li>
          <li>
            “Sensitive Data” means (i) social security number, tax file number,
            passport number, driver’s license number, Voter’s card, National
            Identification Number, or similar identifier (or any portion
            thereof); (ii) credit or debit card number (other than the truncated
            (last four digits) of a credit or debit card); (iii) employment,
            financial, credit, genetic, biometric or health information; (iv)
            racial, ethnic, political or religious affiliation, trade union
            membership, information about sexual life or sexual orientation, or
            criminal record; (v) account passwords; or (vi) other information
            that falls within the definition of “special categories of data”
            under applicable Data Protection Laws.
          </li>
          <li>
            “Sub-processor” means any processor engaged by Company or its
            Affiliates to assist in fulfilling its obligations with respect to
            serving or providing the Website or Products pursuant to the
            Agreement or this DPA. Sub-processors may include third parties or
            Affiliates of Company but shall exclude Company’s employees,
            contractors, or consultants.
          </li>
          <li>
            Unless otherwise defined herein, the terms “personal data,”
            “controller,” “data subject,” “processor” and “processing” shall
            have the meaning given to them under applicable Data Protection Laws
            or if not defined thereunder, the GDPR, and “process,” “processes,”
            and “processed,” with respect to any Customer Data, shall be
            interpreted accordingly.
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">Data Processing</h5>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>Scope and Roles:</strong> The Parties acknowledge and agree
            that with regard to the processing of Customer Data, this DPA
            applies when Customer Data is processed by Company. In this context,
            Company will act as “processor” to Customer who may act either as
            “controller” or “processor” with respect to Customer Data.
          </li>
          <li>
            <strong>Purpose Limitation and Customer Controls:</strong> Company
            shall process Customer Data, as further described in Annex A
            (Details of Data Processing) of this DPA, only in accordance with
            Customer’s documented lawful instructions as set forth in this DPA,
            as necessary to comply with applicable law, or as otherwise agreed
            in writing (“Permitted Purposes”). The Website and Products provide
            Customer with a number of controls, including security features and
            functionalities, that Customer may use to retrieve, correct, delete
            or restrict Customer Data. Without prejudice to Section 6.1,
            Customer may use these controls as technical and organizational
            measures to assist it in connection with its obligations under the
            GDPR, CCPA, CPRA, and all other applicable Data Protection Laws,
            including its obligations relating to responding to requests from
            Data Subjects.
          </li>
          <li>
            <strong>Prohibited Data.</strong> Customer will not provide (or
            cause to be provided) any Sensitive Data to Company for processing
            under the Agreement, and Company will have no liability whatsoever
            for Sensitive Data, whether in connection with a Security Incident
            or otherwise. For the avoidance of doubt, this DPA will not apply to
            Sensitive Data.
          </li>
          <li>
            <strong>Compliance with Laws.</strong>
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                Customer represents and warrants that
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    it has complied, and will continue to comply, with all
                    applicable laws, including Data Protection Laws, in respect
                    of its processing of Customer Data and any processing
                    instructions it issues to Company; and
                  </li>
                  <li>
                    it has provided, and will continue to provide, all notice
                    and has obtained, and will continue to obtain, all consents
                    and rights necessary under Data Protection Laws for Company
                    to process Customer Data for the purposes described in the
                    Agreement. Customer shall have sole responsibility for the
                    accuracy, quality, and legality of Customer Data and the
                    means by which Customer acquired Customer Data. Without
                    prejudice to the generality of the foregoing, Customer
                    agrees that it shall be responsible for complying with all
                    laws (including Data Protection Laws) applicable to any
                    other content created, sent, or managed through the Website
                    or Products, including those relating to obtaining consents
                    (where required) to send emails, the content of the emails
                    and its email deployment practices.
                  </li>
                </ol>
              </li>
              <li>
                Company will comply with all laws, rules and regulations
                applicable to it and binding on it in the performance of this
                DPA.
              </li>
            </ol>
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">Customer Instructions</h5>
        <p className="mb-4">
          The parties agree that the Agreement and this DPA, including the
          provision of instructions via configuration tools such as any Company
          management console and APIs made available by Company for the Website
          and Products, constitute Customer's documented instructions regarding
          Company's processing of Customer Data ("Documented Instructions").
          Company will process Customer Data only in accordance with Documented
          Instructions. Additional instructions outside the scope of the
          Documented Instructions (if any) require prior written agreement
          between Customer and Company, including agreement on any additional
          fees payable by Customer to Company for carrying out such
          instructions. Customer is entitled to terminate this DPA and the
          Agreement if Company declines to follow instructions requested by
          Customer that are outside the scope of, or changed from, those given
          or agreed to be given in this DPA.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Confidentiality of Customer Data
        </h5>
        <p className="mb-4">
          Company will not access or use, or disclose to any third party, any
          Customer Data, except, in each case, as necessary to maintain or
          provide the Website or Products, or as necessary to comply with the
          law or a valid and binding order of a governmental body (such as a
          preservation request, warrant, subpoena or court order). To the extent
          applicable by law, if a governmental body sends a demand for Customer
          Data, Company will attempt to redirect the governmental body to
          request that data directly from Customer. As part of this effort,
          Company may provide Customer’s basic contact information to the
          government body. If compelled to disclose Customer Data to a
          government body, then Company will give Customer reasonable notice of
          the demand to allow Customer to seek a protective order or other
          appropriate remedy unless Company is legally prohibited from doing so.{' '}
        </p>

        <h5 className="text-l font-semibold mb-4">
          Confidentiality Obligations of Company Personnel
        </h5>
        <p className="mb-4">
          Company restricts its personnel from processing Customer Data without
          authorization by Company as described in the Company Security
          Standards. Company imposes appropriate contractual obligations upon
          its personnel, including relevant obligations regarding
          confidentiality, data protection and data security.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Security of Data Processing.
        </h5>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            Company shall implement and maintain appropriate technical and
            organizational security measures that are designed to protect
            Customer Data from Security Incidents and designed to preserve the
            security and confidentiality of Customer Data in accordance with
            Company’s security standards described in Annex B (“Security
            Measures”) of this DPA.
          </li>
          <li>
            Company shall ensure that any person who is authorized by Company to
            process Customer Data (including its employees, agents, and
            subcontractors) shall be under an appropriate obligation of
            confidentiality (whether a contractual or statutory duty).
          </li>
          <li>
            In assessing the appropriate level of security, Company shall take
            account in particular of the risks that are presented by Processing,
            in particular from a Personal Data breach.
          </li>
          <li>
            Customer is responsible for reviewing the information made available
            by Company relating to data security and making an independent
            determination as to whether such meets Customer’s requirements and
            legal obligations under Data Protection Laws. Customer acknowledges
            that the Security Measures are subject to technical progress and
            development and that Company may update or modify the Security
            Measures from time to time, provided that such updates and
            modifications do not result in the degradation of the overall
            security of the Website or Products provided to Customer.
          </li>
          <li>
            Upon becoming aware of a Security Incident, Company shall use
            commercially reasonable efforts to:
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                notify Customer without undue delay, and where feasible, within
                forty-eight (48) hours of awareness;
              </li>
              <li>
                provide timely information relating to the Security Incident as
                it becomes known or as is reasonably requested by Customer; and{' '}
              </li>
              <li>
                promptly take reasonable steps to contain and investigate any
                Security Incident. Company’s notification of or response to a
                Security Incident under this Section shall not be construed as
                an acknowledgment by Company of any fault or liability with
                respect to the Security Incident.
              </li>
            </ol>
          </li>
          <li>
            Notwithstanding the above, Customer agrees that except as provided
            by this DPA, Customer is responsible for its secure use of the
            Website and Products, including securing Customer Account
            authentication credentials, protecting the security of Customer Data
            when in transit to and from the Website or Product, and taking any
            appropriate steps to securely encrypt or backup any Customer Data
            uploaded to the Website or Products.
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">
          Security Reports and Audits
        </h5>
        <p className="mb-4">
          Subject to this Section, Company shall make available to Customer on
          written request all information necessary to demonstrate compliance
          with this Agreement, and shall allow for and contribute to audits,
          including inspections, by Customer or an auditor mandated by Customer
          in relation to the Processing of Customer Data. Information and audit
          rights of Customer only arise under Section to the extent that the
          Agreement or this DPA does not otherwise give them information and
          audit rights meeting the relevant requirements of Data Protection Law.
          Company shall respond to all reasonable written requests for
          information made by Customer to confirm Company’s compliance with this
          DPA, including responses to information security, due diligence, and
          audit questionnaires, by making additional information available
          regarding its information security program upon Customer’s written
          request to Company provided that Customer shall not exercise this
          right more than once per calendar year. By submitting a request
          Customer agrees to the terms of the{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>{' '}
          regarding Customer’s personally identifiable information.
        </p>

        <h5 className="text-l font-semibold mb-4">Sub-Processing</h5>
        <ol className="list-decimal pl-6 mt-2 mb-2">
          <li>
            <strong>Authorized Sub-processors.</strong> Customer acknowledges,
            understands and agrees that Company may use sub-processors to
            fulfill its contractual obligations under this DPA or to provide
            certain services on its behalf, such as providing tracking or
            support services. Company has engaged Sub-processors to carry out
            processing activities on Customer Data on behalf of Customer, as
            amended by Company. Company shall notify Customer if it adds or
            removes Sub-processors prior to any such changes. Company may update
            the Sub-processor list and may provide Customer with a mechanism to
            obtain notice of that update. Customer consents to Company's use of
            Sub-processors as described in this Section. Except as set forth in
            this Section, or as Customer may otherwise authorize, Company will
            not permit any Sub-processor to carry out processing activities on
            Customer Data on behalf of Customer.
          </li>
          <li>
            <strong>Sub-processor Obligations.</strong> Company shall:
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                enter into a written agreement with each Sub-processor
                containing data protection obligations that provide at least the
                same level of protection for Customer Data as those in this DPA,
                to the extent applicable to the nature of the service provided
                by such Sub-processor; and
              </li>
              <li>
                remain responsible for such Sub-processor's compliance with the
                obligations of this DPA and for any acts or omissions of such
                Sub-processor that cause Company to breach any of its
                obligations under this DPA. Customer acknowledges and agrees
                that, where applicable, Company fulfills its obligations under
                clause of the Controller-to-Processor Clauses and
                Processor-to-Processor Clauses (as applicable) by complying with
                this Section and that Company may be prevented from disclosing
                Sub-processor agreements to Customer due to confidentiality
                restrictions but Company shall, upon request, use reasonable
                efforts to provide Customer with all relevant information it
                reasonably can in connection with Sub-processor agreements.
              </li>
            </ol>
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">Data Subject Rights</h5>
        <ol className="list-decimal pl-6 mt-2 mb-2">
          <li>
            <strong>Data Subject Requests:</strong> Taking into account the
            nature of the Processing, processor may assist the Company by
            implementing appropriate technical and organizational measures,
            insofar as this is possible, for the fulfilment of the Company
            obligations, as reasonably understood by Company, to respond to
            requests to exercise Data Subject rights under the Data Protection
            Laws. In the event that any such request is made to Company
            directly, Company shall not respond to such communication directly
            except as appropriate (for example, to direct the Data Subject to
            contact Customer) or legally required, without Customer’s prior
            authorization. If Company is required to respond to such a request,
            Company shall, where Customer is identified or identifiable from the
            request, promptly notify Customer and provide Customer with a copy
            of the request unless Company is legally prohibited from doing so.
            For the avoidance of doubt, nothing in the Agreement (including this
            DPA) shall restrict or prevent Company from responding to any Data
            Subject or data protection authority requests in relation to
            personal data for which Company is a controller.
          </li>
          <li>
            <strong>Data Protection Impact Assessment:</strong>To the extent
            required under applicable Data Protection Laws, Company shall
            (considering the nature of the processing and the information
            available to Company) provide all reasonably requested information
            regarding the Website or Products to enable Customer to carry out
            data protection impact assessments or prior consultations with data
            protection authorities as required by Data Protection Laws. Company
            shall comply with the foregoing by:
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>complying with Section 7 (Security Reports and Audits);</li>
              <li>
                providing the information contained in the Agreement, including
                this DPA; and
              </li>
              <li>
                if the foregoing sub-sections 9.2.1 and 9.2.3 are insufficient
                for Customer to comply with such obligations, upon request,
                providing additional reasonable assistance (at Customer’s sole
                expense).
              </li>
            </ol>
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">
          Security Breach Notification
        </h5>
        <ol className="list-decimal pl-6 mt-2 mb-2">
          <li>
            Company shall notify Customer without undue delay upon Company
            becoming aware of a Personal Data breach affecting Customer’s
            Personal Data, providing Customer with sufficient information to
            allow Customer to meet any obligations to report or inform Data
            Subjects of the Personal Data breach under the Data Protection Laws.
          </li>
          <li>
            Company shall co-operate with Customer and take reasonable
            commercial steps as directed by Customer to assist in the
            investigation, mitigation and remediation of each such Personal Data
            breach.
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">
          Return or Deletion of Data Deletion or Return on Termination
        </h5>
        <p className="mb-4">
          Upon termination or expiration of the Agreement, Company shall (at
          Customer’s election) delete or return to Customer all Customer Data
          (including copies) in its possession or control, except that this
          requirement shall not apply to the extent Company is required by
          applicable law to retain some or all of Customer Data, or to Customer
          Data it has archived on back-up systems, which Customer Data Company
          shall securely isolate, protect from any further processing and
          eventually delete in accordance with Company’s deletion or retention
          policies, except to the extent required by applicable law. The Parties
          agree that the certification of deletion of Customer Data shall be
          provided by Company to Customer only upon Customer’s written request.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Jurisdiction-Specific Terms
        </h5>
        <p className="mb-4">
          To the extent Company processes Customer Data originating from and
          protected by Data Protection Laws in one of the jurisdictions listed
          in Annex C, then the terms specified in Annex C with respect to the
          applicable jurisdiction(s) (“Jurisdiction-Specific Terms”) apply in
          addition to the terms of this DPA. In the event of any conflict or
          ambiguity between the Jurisdiction-Specific Terms and any other terms
          of this DPA, the applicable Jurisdiction-Specific Terms will take
          precedence, but only to the extent of the Jurisdiction-Specific Terms’
          applicability to Company.
        </p>

        <h5 className="text-l font-semibold mb-4">Termination of the DPA</h5>
        <p className="mb-4">
          This DPA shall remain in effect for as long as Company carries out
          Customer Data processing operations on behalf of Customer or until
          termination of the Agreement.
        </p>

        <h5 className="text-l font-semibold mb-4">Limitation of Liability</h5>
        <ol className="list-decimal pl-6 mt-2 mb-2">
          <li>
            Each Party’s and all of its Affiliates’ liability taken together in
            the aggregate arising out of or related to this DPA (shall be
            subject to the exclusions and limitations of liability set forth in
            the Agreement.
          </li>
          <li>
            Any claims made against Company or its Affiliates under or in
            connection with this DPA (including, where applicable,) shall be
            brought solely by Customer.
          </li>
          <li>
            In no event shall any Party limit its liability with respect to any
            individual’s data protection rights under this DPA or otherwise.
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">Duties to Inform</h5>
        <p className="mb-4">
          Where Customer Data becomes subject to confiscation during bankruptcy
          or insolvency proceedings, or similar measures by third parties while
          being processed by Company, Company will inform Customer without undue
          delay. Company will, without undue delay, notify all relevant parties
          in such action (e.g., creditors, bankruptcy trustee) that any Customer
          Data subjected to those proceedings is Customer’s property and area of
          responsibility and that Customer Data is at Customer’s sole
          disposition.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Entire Agreement; Conflict
        </h5>
        <p className="mb-4">
          The Parties agree that this DPA shall replace any existing data
          processing agreement or similar document that the Parties may have
          previously entered into in connection with the Website or Products. In
          the event of any conflict or inconsistency between this DPA and the
          UToU, the provisions of the following documents (in order of
          precedence) shall prevail; then this DPA; and then the UToU.
        </p>

        <h5 className="text-l font-semibold mb-4">Modification</h5>
        <p className="mb-4">
          Except for any changes made by this DPA, the Agreement remains
          unchanged and in full force and effect.
        </p>

        <h5 className="text-l font-semibold mb-4">Successors and Assignees</h5>
        <p className="mb-4">
          No one other than a Party to this DPA, its successors and permitted
          assignees shall have any right to enforce any of its terms.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Governing Law and Jurisdiction
        </h5>
        <p className="mb-4">
          This DPA shall be governed by and construed in accordance with the
          governing law and jurisdiction of Nigeria.
        </p>

        <h5 className="text-l font-semibold mb-4">Effective Date</h5>
        <p className="mb-4">
          This DPA is entered into with effect from the earlier date of use of
          the Website or Products or the signature on the applicable Order Form.
        </p>
      </section>

      <section className="mb-8">
        <h5 className="text-xl font-bold mb-4">
          ANNEX 1 TO CLOUDVANTAGE DATA PROCESSING ADDENDUM
        </h5>
        <h6 className="text-l font-semibold mb-4">
          Company Security Standards
        </h6>
        <p className="mb-4">
          Capitalized terms not otherwise defined in this document have the
          meanings assigned to them in the Agreement or DPA.
        </p>

        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>Information Security Program:</strong> Company will maintain
            an information security program (including the adoption and
            enforcement of internal policies and procedures) designed to (a)
            help Company secure Customer Data against accidental or unlawful
            loss, access or disclosure, (b) identify reasonably foreseeable and
            internal risks to security and unauthorized access to the Company
            Network, and (c) minimize security risks, including through risk
            assessment and regular testing. Company will designate one or more
            employees to coordinate and be accountable for the information
            security program. The information security program will include the
            following measures: The Company Network will be electronically
            accessible to employees, contractors and any other person as
            necessary to provide the Website and Products. Company will maintain
            access controls and policies to manage what access is allowed to the
            Company Network from each network connection and user, including the
            use of firewalls or functionally equivalent technology and
            authentication controls. Company will maintain corrective action and
            incident response plans to respond to potential security threats.
          </li>
          <li>
            <strong>Continued Evaluation:</strong> Company will conduct periodic
            reviews of the security of Company’s Network and adequacy of Data
            Processor’s information security program as measured against
            industry security standards and its policies and procedures. Company
            will continually evaluate the security of Company’s Network to
            determine whether additional or different security measures are
            required to respond to new security risks or findings generated by
            the periodic reviews.
          </li>
        </ol>

        <p className="mb-4">
          The Security Measures implemented by the data importer are as
          described in Annex 1 to the DPA (Company Security Standards).
        </p>
      </section>

      <section className="mb-8">
        <h5 className="text-xl font-bold mb-4">
          ANNEX A TO CLOUDVANTAGE DATA PROCESSING ADDENDUM
        </h5>
        <h6 className="text-l font-semibold mb-4">
          Details of Data Processing
        </h6>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>Categories of Data Subjects:</strong> The categories of Data
            Subjects whose Personal Data is Processed include (a) a user or
            visitor to the Website; (b) Customer (i.e., an individual with
            access to a Customer Account); and (c) a Customer user, visitor,
            customer, subscribe, end-user and other individual about whom
            Customer has given Company information or has otherwise interacted
            with Customer via the Website or Products (collectively, a "Customer
            End-User").
          </li>
          <li>
            <strong>Categories of Personal Data:</strong> Customer or Customer
            End-Users may upload, submit, or otherwise provide certain Personal
            Data via the Website or Products, the extent of which is typically
            determined and controlled by Customer in its sole discretion, and
            may include the following types of Personal data:
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                Identification and contact data (name, address, title, contact
                details, username); financial information (credit card details,
                account details, payment information); employment details
                (employer, job title, geographic location, area of
                responsibility).
              </li>
              <li>
                Identification and contact data (name, date of birth, gender,
                general, occupation or other demographic information, address,
                title, contact details, including email address); personal
                interests or preferences (including purchase history, marketing
                preferences and publicly available social media profile
                information); IT information (IP addresses, usage data, cookies
                data, online navigation data, location data, browser data);
                financial information (credit card details, account details,
                payment information).
              </li>
            </ol>
          </li>
          <li>
            <strong>Sensitive Data Processed (if applicable):</strong> Company
            does not want to, nor does it intentionally, collect or process any
            Sensitive Data in connection with the Website or Products.
          </li>
          <li>
            <strong>Frequency of Processing:</strong> Continuous and as
            determined by Customer.
          </li>
          <li>
            <strong>Subject Matter and Nature of the Processing:</strong>{' '}
            Company provides hosted private cloud services, as more particularly
            described on the Agreement, Website, Products or Order Form. The
            subject matter of the data processing under this DPA is the Customer
            Data. Customer Data will be processed in accordance with the
            Agreement (including this DPA) and may be subject to the following
            processing activities: Storage and other processing necessary to
            provide, maintain and improve the Website and Products provided to
            Customer pursuant to the Agreement.
          </li>
          <li>
            <strong>Purpose of the Processing:</strong> Company shall only
            process Customer Data for the Permitted Purposes, which shall
            include: (a) processing as necessary to provide the Website and
            Products in accordance with the Agreement; (b) processing initiated
            by Customer in its use of the Website and Products; and (c)
            processing to comply with any other reasonable instructions provided
            by Customer (e.g., via email or support tickets) that are consistent
            with the terms of the Agreement.
          </li>
          <li>
            <strong>
              Duration of Processing and Period for which Personal Data will be
              Retained:
            </strong>{' '}
            Company will process Customer Data as outlined in Section 12 (Return
            or Deletion of Data) of this DPA.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h5 className="text-xl font-bold mb-4">
          ANNEX B TO CLOUDVANTAGE DATA PROCESSING ADDENDUM
        </h5>
        <h6 className="text-l font-semibold mb-4">Security Measures</h6>
        <p className="mb-4">
          The Security Measures implemented by the data importer are as
          described in Annex 1 to the DPA (Company Security Standards).
        </p>
      </section>
    </div>
  );
};

export default DataProcessing;
